import { fetchUsers, selectUsers, selectUsersAttractedStatus, selectUsersBlockStatus, selectUsersCurrentGroup, selectUsersCurrentPage, selectUsersDeletedStatus, selectUsersFirstLoad, selectUsersLoading, selectUsersSearch, selectUsersTotalCount, selectUsersTotalPages, setUsersAttractedStatus, setUsersBlockStatus, setUsersDeletedStatus, setUsersPage, setUsersSearch, addUserToReport, removeUserFromReport, clearSelectedUsersForReport, setReportGenerationMode, selectSelectedUsersForReport, selectSelectedUserIdsForReport, selectIsReportGenerationMode } from "@/entities/user"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { PageHeader } from "@/widgets/page-header"
import { PageSubtitle } from "@/shared/ui/page-subtitle"
import { UsersFilters } from "@/widgets/users-filters"
import { UsersTable } from "@/widgets/users-table"
import { ReactNode, useEffect, useRef, useState } from "react"
import { getBonusTransactionsReportXlsx } from "@/shared/api/bonuses/bonuses"
import { selectAvailableBonuses } from "@/entities/bonus"
import { Controller, useForm } from "react-hook-form"
import * as yup from 'yup'
import { yupResolver } from "@hookform/resolvers/yup"
import DatePicker from "react-datepicker"
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"
import { FormInput } from "@/shared/ui/form-input"
import { Modal } from "@/shared/ui/modal"
import { createPortal } from "react-dom"
import { getDisplayName } from "@/entities/user"

interface ReportFormValues {
    start_date?: Date
    end_date?: Date
    bonus_id?: string[]
}

const reportValidationSchema = yup.object().shape({
    start_date: yup.date().required("Укажите дату начала"),
    end_date: yup.date().min(yup.ref('start_date'), "Некорректная дата").required("Укажите дату окончания"),
    bonus_id: yup.array().of(yup.string()).optional()
})

const CalendarContainer = ({ children }: { children: ReactNode }) => {
    return createPortal(children, document.body);
}

export const UsersListPage = () => {
    const isFirstRender = useRef(true)
    const usersFirstLoad = useAppSelector(selectUsersFirstLoad)
    const page = useAppSelector(selectUsersCurrentPage)
    const totalPages = useAppSelector(selectUsersTotalPages)
    const group = useAppSelector(selectUsersCurrentGroup)
    const isLoading = useAppSelector(selectUsersLoading)
    const totalCount = useAppSelector(selectUsersTotalCount)
    const users = useAppSelector(selectUsers)
    const search = useAppSelector(selectUsersSearch)
    const blockStatus = useAppSelector(selectUsersBlockStatus)
    const deletedStatus = useAppSelector(selectUsersDeletedStatus)
    const attractedStatus = useAppSelector(selectUsersAttractedStatus)
    const selectedUsers = useAppSelector(selectSelectedUsersForReport)
    const selectedUserIds = useAppSelector(selectSelectedUserIdsForReport)
    const isReportMode = useAppSelector(selectIsReportGenerationMode)
    const bonuses = useAppSelector(selectAvailableBonuses)
    const [isReportModalOpen, setIsReportModalOpen] = useState(false)
    const dispatch = useAppDispatch()

    const { control, watch, handleSubmit, formState: { isSubmitting } } = useForm({
        defaultValues: {
            start_date: undefined,
            end_date: undefined,
            bonus_id: []
        },
        resolver: yupResolver(reportValidationSchema)
    })

    useEffect(() => {
        if (!isFirstRender.current) {
            dispatch(fetchUsers())
        }
    }, [page, group, search, blockStatus, deletedStatus, attractedStatus])

    useEffect(() => {
        if (usersFirstLoad) {
            dispatch(fetchUsers())
        }
        isFirstRender.current = false
    }, [])

    const bonusSelectOptions = bonuses.map(bonus => ({
        value: bonus.id,
        label: bonus.name
    }))

    const handleCreateReport = async (data: ReportFormValues) => {
        try {
            const responseBlob = await getBonusTransactionsReportXlsx({
                clientIds: selectedUserIds || undefined,
                bonusIds: data.bonus_id || undefined,
                endDate: data.end_date?.toISOString(),
                startDate: data.start_date?.toISOString(),
            })
            const url = window.URL.createObjectURL(new Blob([responseBlob.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('target', '_blank')
            link.setAttribute('download', 'bonus-transactions-report.xlsx')
            document.body.appendChild(link)
            link.click()
            link.parentNode?.removeChild(link)

            dispatch(clearSelectedUsersForReport())
            setIsReportModalOpen(false)
        } catch (error) {
            console.error('Error creating report:', error)
        }
    }

    return (
        <div className="_page_container">
            <PageHeader title="пользователи" />
            <div className="flexbox">
                <PageSubtitle title="список пользователей" />
                {isReportMode && (
                    <div className="p-4 bg-white rounded-3xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-medium text-lg">
                                Режим создания отчета
                            </div>
                            <button
                                onClick={() => {
                                    dispatch(setReportGenerationMode(false))
                                    dispatch(clearSelectedUsersForReport())
                                }}
                                className="px-4 py-2 text-sm rounded-lg border border-color-dark hover:bg-gray-50"
                            >
                                Отменить
                            </button>
                        </div>
                        {selectedUserIds.length > 0 && (
                            <div className="mb-4">
                                <div className="text-sm font-medium mb-2">
                                    Выбрано пользователей: {selectedUserIds.length}
                                </div>
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                    {selectedUsers.map(user => (
                                        <div key={user.id} className="px-3 py-1 bg-color-primary/10 rounded-full text-sm flex items-center gap-2">
                                            <span>{getDisplayName(user)}</span>
                                            <button
                                                onClick={() => dispatch(removeUserFromReport(user.id))}
                                                className="text-color-primary hover:text-color-primary/70"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <button
                            onClick={() => setIsReportModalOpen(true)}
                            disabled={selectedUserIds.length === 0}
                            className="w-full py-3 text-lg rounded-xl font-bold text-white bg-color-primary hover:bg-color-primary/70 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            Создать отчет
                        </button>
                    </div>
                )}
                {!isReportMode && (
                    <div>
                        <button
                            onClick={() => dispatch(setReportGenerationMode(true))}
                            className="px-6 py-3 text-lg rounded-xl font-bold text-white bg-color-secondary hover:bg-color-primary transition"
                        >
                            Создать отчет по пользователям
                        </button>
                    </div>
                )}
                <UsersFilters
                    search={search}
                    setSearch={(query) => dispatch(setUsersSearch(query))}
                    blockStatus={blockStatus}
                    setBlockStatus={(value) => dispatch(setUsersBlockStatus(value))}
                    deletedStatus={deletedStatus}
                    setDeletedStatus={(value) => dispatch(setUsersDeletedStatus(value))}
                    attractedStatus={attractedStatus}
                    setAttractedStatus={(value) => dispatch(setUsersAttractedStatus(value))}
                />
                <UsersTable
                    page={page}
                    totalPages={totalPages}
                    users={users}
                    totalCount={totalCount}
                    isLoading={isLoading}
                    setPage={(page) => dispatch(setUsersPage(page))}
                    onAccrual={() => dispatch(fetchUsers())}
                    labelNoInfo={
                        deletedStatus === 'deleted'
                            ? 'Нет удалённых пользователей'
                            : 'Нет данных о пользователях'
                    }
                    isReportMode={isReportMode}
                    selectedUserIds={selectedUserIds}
                    onUserSelect={(user) => dispatch(addUserToReport(user))}
                    onUserDeselect={(userId) => dispatch(removeUserFromReport(userId))}
                />
            </div>
            <Modal
                isOpen={isReportModalOpen}
                title="Создание отчета"
                onRequestClose={() => setIsReportModalOpen(false)}
            >
                <form onSubmit={handleSubmit(handleCreateReport)} className="flex flex-col gap-4">
                    <div>
                        <div className="mb-1 font-medium">
                            Начальная дата
                        </div>
                        <Controller
                            control={control}
                            name="start_date"
                            render={({ field }) => (
                                <DatePicker
                                    selected={field.value}
                                    onChange={(date: Date | null) => field.onChange(date)}
                                    wrapperClassName="react-datepicker-form2"
                                    popperClassName="z-[9999]"
                                    popperContainer={CalendarContainer}
                                    dateFormat={"dd.MM.yyyy"}
                                    showIcon
                                    placeholderText="Выберите начальную дату"
                                    enableTabLoop={false}
                                    popperProps={{
                                        placement: 'bottom'
                                    }}
                                    icon={(<CalendarIcon width={24} height={24} />)}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <div className="mb-1 font-medium">
                            Конечная дата
                        </div>
                        <Controller
                            control={control}
                            name="end_date"
                            render={({ field }) => (
                                <DatePicker
                                    selected={field.value}
                                    minDate={watch('start_date')}
                                    onChange={(date: Date | null) => field.onChange(date)}
                                    wrapperClassName="react-datepicker-form2"
                                    popperClassName="z-[9999]"
                                    popperContainer={CalendarContainer}
                                    dateFormat={"dd.MM.yyyy"}
                                    showIcon
                                    placeholderText="Выберите конечную дату"
                                    enableTabLoop={false}
                                    popperProps={{
                                        placement: 'bottom'
                                    }}
                                    icon={(<CalendarIcon width={24} height={24} />)}
                                />
                            )}
                        />
                    </div>
                    <FormInput
                        label="Бонусы (необязательно)"
                        id="bonus_id"
                        options={bonusSelectOptions}
                        control={control}
                        required={false}
                        type="select"
                        isMulti={true}
                        placeholder="Выберите бонусы"
                    />
                    <button
                        type="submit"
                        className="w-full py-3 text-lg rounded-xl font-bold text-white bg-color-primary hover:bg-color-primary/70 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Создание...' : 'Создать отчет'}
                    </button>
                </form>
            </Modal>
        </div>
    )
}
